import { Logger } from '@eluve/logger';

import {
  APPOINTMENT_MANIFEST_TTL_MS,
  AppointmentManifestFile,
  AppointmentManifestIdbRow,
  DexieDb,
} from './files-db';

/** Finalize-time stats merged into a manifest entry once a file closes. */
export type AppointmentManifestFileStats = Pick<
  AppointmentManifestFile,
  'byteSize' | 'packetCount' | 'firstPacketAt' | 'lastPacketAt'
>;

/**
 * Append a produced audio file to an appointment's durable manifest. Idempotent
 * per `fileName` so re-creating the same chunk (actor re-entry) doesn't duplicate
 * the entry. Recorded at file creation with null stats; `updateAppointment
 * ManifestFileStats` fills them in at finalize. Persists beyond the audio bytes
 * so the full set of produced files is available at completion time.
 */
export const recordAppointmentManifestFile = async (
  db: DexieDb,
  appointmentId: string,
  file: Pick<
    AppointmentManifestFile,
    'fileName' | 'recordingStartedAt' | 'chunkNum' | 'wasDegraded'
  >,
): Promise<void> => {
  await db.transaction('rw', db.appointmentManifests, async () => {
    const existing = await db.appointmentManifests.get(appointmentId);
    const files = existing?.files ?? [];
    if (files.some((f) => f.fileName === file.fileName)) {
      return;
    }
    const row: AppointmentManifestIdbRow = {
      appointmentId,
      files: [
        ...files,
        {
          ...file,
          byteSize: null,
          packetCount: null,
          firstPacketAt: null,
          lastPacketAt: null,
        },
      ],
      updatedAt: new Date().toISOString(),
    };
    await db.appointmentManifests.put(row);
  });
};

/**
 * Merge finalize-time stats (size / packet count / write window) into an
 * existing manifest entry. Creates the entry if the file was never recorded
 * (defensive — recording normally precedes finalize). [ENG-4717]
 */
export const updateAppointmentManifestFileStats = async (
  db: DexieDb,
  appointmentId: string,
  file: Pick<
    AppointmentManifestFile,
    'fileName' | 'recordingStartedAt' | 'chunkNum'
  > &
    AppointmentManifestFileStats,
): Promise<void> => {
  await db.transaction('rw', db.appointmentManifests, async () => {
    const existing = await db.appointmentManifests.get(appointmentId);
    const files = existing?.files ?? [];
    const idx = files.findIndex((f) => f.fileName === file.fileName);
    const next =
      idx >= 0
        ? files.map((f, i) => (i === idx ? { ...f, ...file } : f))
        : [...files, file];
    await db.appointmentManifests.put({
      appointmentId,
      files: next,
      updatedAt: new Date().toISOString(),
    });
  });
};

/** Read an appointment's manifest, or null if none exists locally. */
export const getAppointmentManifest = async (
  db: DexieDb,
  appointmentId: string,
): Promise<AppointmentManifestIdbRow | null> =>
  (await db.appointmentManifests.get(appointmentId)) ?? null;

/** Remove an appointment's manifest (e.g. once the server has acknowledged it). */
export const deleteAppointmentManifest = async (
  db: DexieDb,
  appointmentId: string,
): Promise<void> => {
  await db.appointmentManifests.delete(appointmentId);
};

/**
 * Delete manifests older than the TTL. Best-effort cleanup invoked from the
 * periodic local file sync.
 */
export const sweepStaleAppointmentManifests = async (
  db: DexieDb,
  logger: Logger,
  now: number = Date.now(),
): Promise<void> => {
  const cutoff = new Date(now - APPOINTMENT_MANIFEST_TTL_MS).toISOString();
  try {
    const deleted = await db.appointmentManifests
      .where('updatedAt')
      .below(cutoff)
      .delete();
    if (deleted > 0) {
      logger.info('Swept stale appointment manifests', { deleted });
    }
  } catch (error) {
    logger.warn('Failed to sweep stale appointment manifests', { error });
  }
};
